import { useState, useEffect } from 'react'
import { useBuildingStore } from '../../stores/buildingStore'
import { useClientStore } from '../../stores/clientStore'
import { useCanAccess } from '../../hooks/useCanAccess'
import { siteApi } from '../../services/siteApi'
import IFCImportButton from './IFCImportButton'
import type { Site } from '../../types'

export default function SiteManagement() {
  const sites = useBuildingStore((s) => s.sites)
  const fetchSites = useBuildingStore((s) => s.fetchSites)
  const clients = useClientStore((s) => s.clients)

  const canCreate = useCanAccess('sites', 'create')
  const canUpdate = useCanAccess('sites', 'update')
  const canDelete = useCanAccess('sites', 'delete')

  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState({ name: '', client_id: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filterClientId, setFilterClientId] = useState('')

  useEffect(() => {
    if (sites.length === 0) fetchSites()
  }, [sites.length, fetchSites])

  const filtered = filterClientId
    ? sites.filter((s) => s.client_id === filterClientId)
    : sites

  const openCreate = () => {
    setForm({ name: '', client_id: filterClientId })
    setEditingId(null)
    setError(null)
    setShowModal(true)
  }

  const openEdit = (site: Site) => {
    setForm({ name: site.name, client_id: site.client_id ?? '' })
    setEditingId(site.id)
    setError(null)
    setShowModal(true)
  }

  const handleSave = async () => {
    if (!form.name.trim()) return
    setSaving(true)
    setError(null)
    try {
      if (editingId) {
        await siteApi.updateSite(editingId, {
          name: form.name.trim(),
          client_id: form.client_id || undefined,
        })
      } else {
        if (!form.client_id) {
          setError('Le client est obligatoire')
          setSaving(false)
          return
        }
        await siteApi.createSite({
          client_id: form.client_id,
          name: form.name.trim(),
        })
      }
      await fetchSites()
      setShowModal(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur sauvegarde')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (site: Site) => {
    if (!confirm(`Supprimer le site "${site.name}" ?`)) return
    try {
      await siteApi.deleteSite(site.id)
      await fetchSites()
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur suppression')
    }
  }

  return (
    <div className="admin-section">
      <div className="admin-section__header">
        <h3>Sites</h3>
        <div className="admin-section__actions">
          {clients.length > 0 && (
            <select
              className="site-selector"
              value={filterClientId}
              onChange={(e) => setFilterClientId(e.target.value)}
            >
              <option value="">Tous les clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <IFCImportButton />
          {canCreate && (
            <button className="admin-btn admin-btn--create" onClick={openCreate}>
              Nouveau site
            </button>
          )}
        </div>
      </div>

      <table className="admin-table">
        <thead>
          <tr>
            <th>Nom</th>
            <th>Client</th>
            <th>IFC</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map((s) => (
            <tr key={s.id}>
              <td>{s.name}</td>
              <td>{s.client_name ?? '-'}</td>
              <td>
                <span className={`admin-badge ${s.is_ifc_source ? 'admin-badge--green' : 'admin-badge--red'}`}>
                  {s.is_ifc_source ? 'Oui' : 'Non'}
                </span>
              </td>
              <td className="admin-table__actions">
                {canUpdate && (
                  <button className="admin-btn admin-btn--small" onClick={() => openEdit(s)}>
                    Editer
                  </button>
                )}
                {canDelete && (
                  <button className="admin-btn admin-btn--small admin-btn--danger" onClick={() => handleDelete(s)}>
                    Supprimer
                  </button>
                )}
              </td>
            </tr>
          ))}
          {filtered.length === 0 && (
            <tr><td colSpan={4} className="admin-table__empty">Aucun site</td></tr>
          )}
        </tbody>
      </table>

      {showModal && (
        <div className="admin-modal__overlay" onClick={() => setShowModal(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingId ? 'Editer site' : 'Nouveau site'}</h3>
            <div className="admin-form">
              <div className="admin-form__field">
                <label>Nom</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  autoFocus
                />
              </div>
              {clients.length > 0 && (
                <div className="admin-form__field">
                  <label>Client</label>
                  <select
                    value={form.client_id}
                    onChange={(e) => setForm((p) => ({ ...p, client_id: e.target.value }))}
                  >
                    <option value="">-- Aucun --</option>
                    {clients.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {error && <div className="admin-error">{error}</div>}
              <div className="admin-form__actions">
                <button className="admin-btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button
                  className="admin-btn admin-btn--create"
                  onClick={handleSave}
                  disabled={saving || !form.name.trim()}
                >
                  {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
